import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { InviteVisitorForm } from '@/components/InviteVisitorForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnterpriseContext } from '@/hooks/useEnterpriseContext';
import { cancelInvitation, getInvitations } from '@/services/api.client';
import type { Invitation } from '@/types';
import { formatDateTime, formatStatusLabel, statusBadgeClass } from '@/lib/utils';

// ─── Time-bucket helper ──────────────────────────────────────────────────────
//
// Today / Upcoming / Past split, computed against the visitor's window. An
// invitation belongs to:
//   - 'today'    when its window overlaps today (inclusive on both ends)
//   - 'upcoming' when its window starts after today
//   - 'past'     when its window ended before today

type Bucket = 'today' | 'upcoming' | 'past';

function bucketFor(inv: Invitation, dayStart: Date, dayEnd: Date): Bucket {
  const from = inv.validFrom ? new Date(inv.validFrom) : null;
  const until = inv.validUntil ? new Date(inv.validUntil) : null;
  if (until && until < dayStart) return 'past';
  if (from && from >= dayEnd) return 'upcoming';
  return 'today';
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function EnterpriseVisitorsPage() {
  const ctx = useEnterpriseContext();
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<Bucket>('today');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invitations', ctx?.propertyId],
    queryFn: () => (ctx ? getInvitations(ctx.propertyId, { limit: 100 }) : null),
    enabled: !!ctx,
  });

  const cancelMut = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      ctx
        ? cancelInvitation(ctx.propertyId, id)
        : Promise.reject(new Error('Missing enterprise context')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations', ctx?.propertyId] });
      toast.success('Invitation cancelled');
    },
    onError: () => toast.error('Failed to cancel invitation'),
  });

  // Bucket + filter invitations once per render. The server already scopes
  // to this enterprise tenant's unit (see invitation.service.ts) — no resident
  // invitations can leak in here.
  const { todayList, upcomingList, pastList, total } = useMemo(() => {
    const all = data?.data ?? [];
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    const q = search.trim().toLowerCase();
    const matchesSearch = (inv: Invitation): boolean =>
      !q || inv.visitorName.toLowerCase().includes(q);

    const today: Invitation[] = [];
    const upcoming: Invitation[] = [];
    const past: Invitation[] = [];
    for (const inv of all) {
      if (!matchesSearch(inv)) continue;
      const b = bucketFor(inv, dayStart, dayEnd);
      if (b === 'today') today.push(inv);
      else if (b === 'upcoming') upcoming.push(inv);
      else past.push(inv);
    }
    return {
      todayList: today,
      upcomingList: upcoming,
      pastList: past,
      total: all.length,
    };
  }, [data, search]);

  const visible = tab === 'today' ? todayList : tab === 'upcoming' ? upcomingList : pastList;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl">Company Visitors</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            All invitations issued by your company {total > 0 && `· ${total}`}
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Plus className="size-4 mr-2" />
          Invite Visitor
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <Tabs value={tab} onValueChange={(v) => setTab(v as Bucket)}>
          <TabsList>
            <TabsTrigger value="today">Today ({todayList.length})</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming ({upcomingList.length})</TabsTrigger>
            <TabsTrigger value="past">Past ({pastList.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="today" />
          <TabsContent value="upcoming" />
          <TabsContent value="past" />
        </Tabs>
        <Input
          placeholder="Search visitor name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-xs"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base capitalize">{tab} invitations</CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <div
              role="alert"
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
            >
              <p className="text-sm font-medium">Couldn&apos;t load invitations</p>
              <p className="text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : visible.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Visitor</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Gate</TableHead>
                  <TableHead>Valid From</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.visitorName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground capitalize">
                      {inv.invitationType.replace('_', ' ')}
                    </TableCell>
                    <TableCell>{inv.gateName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(inv.validFrom)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(inv.validUntil)}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusBadgeClass(inv.status)}>
                        {formatStatusLabel(inv.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {inv.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => cancelMut.mutate({ id: inv.id })}
                          disabled={cancelMut.isPending && cancelMut.variables?.id === inv.id}
                        >
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-10 text-center">
              <Users className="size-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {tab === 'today' && 'No visitors expected today.'}
                {tab === 'upcoming' && 'No upcoming invitations.'}
                {tab === 'past' && 'No past invitations.'}
              </p>
              {tab === 'today' && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setInviteOpen(true)}
                  className="mt-1"
                >
                  Invite someone
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Invite Company Visitor</DialogTitle>
          </DialogHeader>
          {ctx && (
            <InviteVisitorForm
              propertyId={ctx.propertyId}
              scope="enterprise"
              onSuccess={() => {
                void qc.invalidateQueries({ queryKey: ['invitations', ctx.propertyId] });
                setInviteOpen(false);
              }}
              onCancel={() => setInviteOpen(false)}
              submitLabel="Invite"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
